import { type AnyGraphNode, findEdgeNode, nodeName } from '../types/graphNodes';
import { isFlaggedForLanguage } from '../utils/languages';
import { deityAsciiName, isPlaceholderDeity } from './deityIdentity';
import { meaningNodeIdFor } from './meaning';

export interface AudioSources {
  appleTrackId?: string;
  appleCollectionId?: string;
  spotifyTrackId?: string;
  previewUrl?: string;
  durationMs?: number;
  trackName?: string;
  artworkUrl?: string;
}

/**
 * One BHAJAN_FACT node, resolved for the language currently on screen.
 *
 * The API collapses `enText`/`teText`/… down to a single `text` for the
 * requested `?l=`, so a fact carries exactly one language at a time and
 * switching languages refetches rather than re-picking a field.
 */
export interface BhajanFact {
  nodeId: number;
  text: string;
  /** AWS Translate produced this language's text; a human did not write it. */
  autoTranslated: boolean;
}

/**
 * View-model for the bhajan detail page, derived entirely from the content
 * catalog (the `allNonLyrics` graph the app already loads into Redux).
 *
 * Everything a detail page needs lives in the catalog EXCEPT the lyrics text
 * (node type LYRICS is excluded from `allNonLyrics` by design) — so the lyrics
 * node id is surfaced here and fetched separately by the component.
 */
export interface BhajanDetailVM {
  found: boolean;
  bhajanId: number | null;
  title: string | null;
  ragam: string | null;
  ragamId: number | null;
  talam: string | null;
  talamId: number | null;
  beatPattern: string | null;
  deity: string | null;
  /**
   * The ASCII (`normalizedName`) deity name, regardless of the language on
   * screen. `deity` above is what the reader sees — Telugu/Kannada/… script
   * outside English — so it can never be compared against an English word.
   * Anything that DECIDES from the deity reads this instead; see deityIdentity.
   */
  deityNameEn: string | null;
  deityId: number | null;
  composer: string | null;
  composerId: number | null;
  language: string | null;
  bhajanBabyYouTube: string | null;
  audio: AudioSources | null;
  lyricsNodeId: number | null;
  westernNotesNodeId: number | null;
  /**
   * The MEANING node, from the `meaning` edge. Only ever an id here: the slim
   * index strips meaning `data` to nothing and the hydration batch is
   * language-trimmed, so the text needs its own untrimmed fetch — see
   * services/meaningsApi.
   */
  meaningNodeId: number | null;
  facts: BhajanFact[];
}

const EMPTY_VM: BhajanDetailVM = {
  found: false,
  bhajanId: null,
  title: null,
  ragam: null,
  ragamId: null,
  talam: null,
  talamId: null,
  beatPattern: null,
  deity: null,
  deityNameEn: null,
  deityId: null,
  composer: null,
  composerId: null,
  language: null,
  bhajanBabyYouTube: null,
  audio: null,
  lyricsNodeId: null,
  westernNotesNodeId: null,
  meaningNodeId: null,
  facts: [],
};

const asString = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

const httpUrl = (v: unknown): string | null =>
  typeof v === 'string' && v.startsWith('http') ? v : null;

/** audible -> track + album, all resolved from the catalog. */
function deriveAudio(bhajan: AnyGraphNode, nodes: AnyGraphNode[]): AudioSources | null {
  const audible = findEdgeNode(bhajan, 'audible', nodes);
  if (!audible) return null;

  const track = findEdgeNode(audible, 'track', nodes);
  const album = findEdgeNode(audible, 'album', nodes);
  const td = (track?.data ?? {}) as Record<string, unknown>;
  const ad = (album?.data ?? {}) as Record<string, unknown>;

  const durRaw = td.trackDurationMillis;
  const durationMs =
    typeof durRaw === 'string' ? parseInt(durRaw, 10) : Number(durRaw) || 0;

  return {
    appleTrackId: td.trackId != null ? String(td.trackId) : undefined,
    appleCollectionId: ad.collectionId != null ? String(ad.collectionId) : undefined,
    spotifyTrackId: td.spotifyTrackId != null ? String(td.spotifyTrackId) : undefined,
    previewUrl: httpUrl(td.previewUrl) ?? undefined,
    durationMs,
    trackName: asString(td.name) ?? undefined,
    artworkUrl: asString(ad.artUrl100) ?? asString(ad.artUrl60) ?? undefined,
  };
}

/**
 * Every BHAJAN_FACT hanging off this bhajan, in edge order.
 *
 * A fact with no text is dropped rather than rendered as an empty row: the
 * node exists in every language but only the ones an editor (or AWS Translate)
 * has filled in come back with content. Same rule jnana-native applies.
 *
 * Note this reads `fact` nodes out of the SAME hydrated array the rest of the
 * view-model uses. Those nodes are already fetched — collectDetailNodeIds
 * pulls every non-lyrics 1-hop neighbour — but a caller that passes slim index
 * nodes gets no facts, because the slim projection keeps only name fields.
 */
function deriveFacts(
  bhajan: AnyGraphNode,
  nodes: AnyGraphNode[],
  language: string
): BhajanFact[] {
  const facts: BhajanFact[] = [];
  // `fact` is a multi-cardinality edge with nothing stopping an editor from
  // linking the same node twice; a duplicate would collide on the React key.
  const seen = new Set<number>();
  for (const edge of bhajan.edges ?? []) {
    if (edge.name !== 'fact') continue;
    if (seen.has(edge.nodeId)) continue;
    const node = nodes.find((n) => n.nodeId === edge.nodeId);
    const text = asString((node?.data as Record<string, unknown> | undefined)?.text);
    if (!node || !text) continue;
    seen.add(edge.nodeId);
    facts.push({
      nodeId: node.nodeId,
      text,
      autoTranslated: isFlaggedForLanguage(
        (node.data as Record<string, unknown>).autoTranslatedFlags,
        language
      ),
    });
  }
  return facts;
}

export function deriveBhajanDetail(
  nodes: AnyGraphNode[],
  bhajanId: number,
  language: string
): BhajanDetailVM {
  const bhajan = nodes.find((n) => n.nodeId === bhajanId);
  if (!bhajan) return EMPTY_VM;

  const data = (bhajan.data ?? {}) as Record<string, unknown>;
  // Title is language-specific: English reads the normalized (ASCII) name,
  // other scripts read the native `name`. The fallback and every edge label
  // below take the SAME `language` argument rather than nodeName's
  // display-language default, so a caller that asks for one language cannot
  // get a title in it and labels in another.
  const title =
    (language === 'en' ? asString(data.normalizedName) : asString(data.name)) ??
    nodeName(bhajan, language) ??
    null;

  const ragamNode = findEdgeNode(bhajan, 'ragam', nodes);
  const talamNode = findEdgeNode(bhajan, 'talam', nodes);
  const deityNode = findEdgeNode(bhajan, 'deity', nodes);
  const composerNode = findEdgeNode(bhajan, 'composer', nodes);
  const langNode = findEdgeNode(bhajan, 'compositionLanguage', nodes);
  const bbNode = findEdgeNode(bhajan, 'bb', nodes);
  const lyricsEdge = bhajan.edges?.find((e) => e.name === 'lyrics');
  const westernNotesEdge = bhajan.edges?.find((e) => e.name === 'westernNotes');

  const bbData = (bbNode?.data ?? {}) as Record<string, unknown>;

  // A placeholder deity is dropped once, here, rather than by each consumer.
  // The detail page renders this value in a metadata row AND hands it to the
  // Bhajan Babies card, which uses it in its screen-reader label — when only
  // the row applied the rule, the card still announced "…video for stotram".
  const deityIsPlaceholder = isPlaceholderDeity(
    deityNode?.nodeId,
    deityAsciiName(deityNode)
  );

  return {
    found: true,
    bhajanId: bhajan.nodeId,
    title,
    ragam: nodeName(ragamNode, language) ?? null,
    ragamId: ragamNode?.nodeId ?? null,
    talam: nodeName(talamNode, language) ?? null,
    talamId: talamNode?.nodeId ?? null,
    beatPattern: asString((talamNode?.data as Record<string, unknown>)?.beatPattern),
    deity: deityIsPlaceholder ? null : nodeName(deityNode, language) ?? null,
    deityNameEn: deityIsPlaceholder ? null : deityAsciiName(deityNode) ?? null,
    deityId: deityIsPlaceholder ? null : deityNode?.nodeId ?? null,
    composer: nodeName(composerNode, language) ?? null,
    composerId: composerNode?.nodeId ?? null,
    language: nodeName(langNode, language) ?? null,
    bhajanBabyYouTube: httpUrl(bbData.ytLink) ?? httpUrl(bbData.youtubeLink),
    audio: deriveAudio(bhajan, nodes),
    lyricsNodeId: lyricsEdge?.nodeId ?? null,
    westernNotesNodeId: westernNotesEdge?.nodeId ?? null,
    meaningNodeId: meaningNodeIdFor(bhajan),
    facts: deriveFacts(bhajan, nodes, language),
  };
}
